import { useParams } from 'react-router-dom';
import { usePricingElasticity, usePortfolioPricing } from '@/hooks/usePricing';
import type { ProductElasticity, PortfolioPricing } from '@/api/client';
import { LoadingPage, LoadingCard } from '@/components/ui/LoadingSpinner';
import { ErrorPage } from '@/components/ui/ErrorMessage';
import { ExplainabilityCard, generateElasticityExplanation } from '@/components/ui/ExplainabilityCard';
import { MetricTooltip } from '@/components/ui/Tooltip';
import { SectionHeaderWithFreshness, PROVENANCE_PRESETS } from '@/components/ui';

function getElasticityColor(elasticity: number): string {
  const abs = Math.abs(elasticity);
  if (abs > 1.5) return 'text-danger-400';
  if (abs > 1) return 'text-warning-400';
  return 'text-success-400';
}

function getElasticityLabel(classification: string): string {
  switch (classification) {
    case 'elastic': return 'Elastic';
    case 'highly_elastic': return 'Highly Elastic';
    case 'inelastic': return 'Inelastic';
    case 'unit_elastic': return 'Unit Elastic';
    default: return classification;
  }
}

function formatGap(gap: number): { text: string; color: string } {
  const percent = gap * 100;
  if (percent > 5) return { text: `+${percent.toFixed(0)}%`, color: 'text-danger-400' };
  if (percent < -5) return { text: `${percent.toFixed(0)}%`, color: 'text-success-400' };
  return { text: `${percent >= 0 ? '+' : ''}${percent.toFixed(0)}%`, color: 'text-text-secondary' };
}

export function PricingPage() {
  const { clientId } = useParams();
  const elasticityQuery = usePricingElasticity(clientId);
  const portfolioQuery = usePortfolioPricing(clientId);

  if (elasticityQuery.isLoading) {
    return <LoadingPage message="Loading pricing data..." />;
  }

  if (elasticityQuery.error) {
    return <ErrorPage error={elasticityQuery.error} onRetry={() => elasticityQuery.refetch()} />;
  }

  const elasticities = elasticityQuery.data || [];
  const portfolio = portfolioQuery.data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-white mb-6">Pricing Analysis</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Price Elasticity */}
        <div className="card p-6">
          <SectionHeaderWithFreshness
            title="Price Elasticity"
            provenance={PROVENANCE_PRESETS.syntheticModel(new Date())}
          >
            <MetricTooltip metric="elasticity" position="left" />
          </SectionHeaderWithFreshness>
          {elasticities.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {elasticities.map((product: ProductElasticity) => {
                  const elasticityValue = product.own_elasticity ?? product.elasticity ?? 0;
                  const classification = product.interpretation?.sensitivity ?? product.classification ?? 'elastic';
                  const confidence = product.confidence ?? 0.5;
                  return (
                    <div key={product.gtin} className="p-3 bg-surface-600 rounded-lg flex justify-between items-center">
                      <div>
                        <div className="text-white font-medium">{product.name}</div>
                        <div className="text-xs text-text-tertiary font-mono">{product.gtin}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${getElasticityColor(elasticityValue)}`}>
                          {elasticityValue.toFixed(2)}
                        </div>
                        <div className="text-xs text-text-secondary">{getElasticityLabel(classification)}</div>
                        <div className="text-xs text-text-tertiary">{(confidence * 100).toFixed(0)}% conf</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Explainability Card - Elasticity Interpretation */}
              {(() => {
                const avgElasticity = elasticities.reduce((sum, p) =>
                  sum + (p.own_elasticity ?? p.elasticity ?? 0), 0) / elasticities.length;
                return (
                  <ExplainabilityCard
                    {...generateElasticityExplanation(avgElasticity, {
                      productName: 'Portfolio average',
                      category: 'CPG',
                      isPremium: portfolio?.architecture?.average_price ? portfolio.architecture.average_price > 5 : false,
                      hasPromoHistory: true,
                    })}
                  />
                );
              })()}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
              <span className="text-text-tertiary">No elasticity data available</span>
            </div>
          )}
        </div>

        {/* Price Architecture Summary */}
        {portfolioQuery.isLoading ? (
          <LoadingCard />
        ) : portfolio?.architecture ? (
          <div className="card p-6">
            <SectionHeaderWithFreshness
              title="Price Architecture"
              provenance={PROVENANCE_PRESETS.clientData(new Date('2025-12-01'))}
            />
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-surface-600 rounded-lg">
                  <div className="text-sm text-text-secondary">Price Range</div>
                  <div className="text-white font-medium">
                    {portfolio.architecture.price_range
                      ? `$${portfolio.architecture.price_range.min.toFixed(2)} - $${portfolio.architecture.price_range.max.toFixed(2)}`
                      : 'N/A'}
                  </div>
                </div>
                <div className="p-3 bg-surface-600 rounded-lg">
                  <div className="text-sm text-text-secondary">Average Price</div>
                  <div className="text-white font-medium">
                    {portfolio.architecture.average_price != null
                      ? `$${portfolio.architecture.average_price.toFixed(2)}`
                      : 'N/A'}
                  </div>
                </div>
              </div>
              <div className="p-3 bg-surface-600 rounded-lg">
                <div className="text-sm text-text-secondary">Price Variance</div>
                <div className="text-white font-medium">
                  {portfolio.architecture.price_variance != null
                    ? `${(portfolio.architecture.price_variance * 100).toFixed(1)}%`
                    : 'N/A'}
                </div>
                <div className="text-xs text-text-tertiary mt-1">
                  {portfolio.architecture.price_variance != null
                    ? (portfolio.architecture.price_variance > 0.3 ? 'High variance - consider price ladder review' : 'Healthy price distribution')
                    : 'No variance data'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Price Architecture</h3>
            <div className="h-48 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
              <span className="text-text-tertiary">No architecture data available</span>
            </div>
          </div>
        )}

        {/* Portfolio Pricing Table */}
        <div className="card p-6 lg:col-span-2">
          <SectionHeaderWithFreshness
            title="Portfolio Pricing"
            provenance={PROVENANCE_PRESETS.syntheticModel(new Date())}
          >
            <span className="text-xs text-text-tertiary">Model-optimized recommendations</span>
          </SectionHeaderWithFreshness>
          {portfolioQuery.isLoading ? (
            <div className="space-y-2">
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
            </div>
          ) : portfolio?.products && portfolio.products.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-400/30">
                    <th className="text-left py-2 text-text-secondary font-medium">Product</th>
                    <th className="text-right py-2 text-text-secondary font-medium">Current Price</th>
                    <th className="text-right py-2 text-text-secondary font-medium">
                      <div className="flex items-center justify-end gap-1">
                        Elasticity
                        <MetricTooltip metric="elasticity" position="bottom" />
                      </div>
                    </th>
                    <th className="text-right py-2 text-text-secondary font-medium">Optimal Price</th>
                    <th className="text-right py-2 text-text-secondary font-medium">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.products.map((product: PortfolioPricing['products'][number], index: number) => {
                    const gap = formatGap(product.price_gap);
                    return (
                      <tr key={product.gtin} className={index < portfolio.products.length - 1 ? 'border-b border-surface-400/20' : ''}>
                        <td className="py-3">
                          <div className="text-white font-medium">{product.name}</div>
                          <div className="text-xs text-text-tertiary font-mono">{product.gtin}</div>
                        </td>
                        <td className="py-3 text-right text-text-secondary">${product.current_price.toFixed(2)}</td>
                        <td className={`py-3 text-right ${getElasticityColor(product.elasticity)}`}>
                          {product.elasticity.toFixed(2)}
                        </td>
                        <td className="py-3 text-right text-primary-400">${product.optimal_price.toFixed(2)}</td>
                        <td className={`py-3 text-right ${gap.color}`}>{gap.text}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-text-secondary">No portfolio pricing data available</p>
          )}
        </div>
      </div>
    </div>
  );
}
